import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  CUSTOM_ROLE_NAME_MAX,
  GRANTABLE_PERMISSIONS,
  matrixRows,
  type CustomResellerRoleKey,
  type EditableResellerRole,
  type MatrixRoleKey,
  type ResellerRoleMatrix,
  type ResellerRoleRow,
} from "@xop/dashboard-shared";
import { ChevronDown, Plus } from "lucide-react";
import { useState } from "react";
import type { FormEvent, JSX } from "react";

import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ApiError } from "@/lib/api";

/** "N people still have this role — …" for crm-api's 409 `ROLE_IN_USE`. */
export function roleInUseMessage(count: number | undefined): string {
  const who =
    count === undefined ? "People still have" : count === 1 ? "1 person still has" : `${count} people still have`;
  return `${who} this role — move them to another role first.`;
}

/** `member_count` from a 409, wherever the BFF put it: lifted into `details`,
 * or still inside the raw upstream body. */
function memberCount(e: ApiError): number | undefined {
  const d = e.details as
    | { member_count?: unknown; upstream_body?: { details?: { member_count?: unknown } } }
    | undefined;
  const value = d?.member_count ?? d?.upstream_body?.details?.member_count;
  return typeof value === "number" ? value : undefined;
}

/** One sentence for a failed role write — the specific refusals crm-api
 * names, then its own message, then "unreachable". */
export function roleWriteError(e: unknown, companyName: string, name?: string): string {
  if (!(e instanceof ApiError)) return "The CRM was unreachable.";
  if (e.code === "ROLE_IN_USE") return roleInUseMessage(memberCount(e));
  if (e.code === "ROLE_NAME_TAKEN") {
    return `${name ? `“${name}”` : "That name"} is already a role at ${companyName}. Pick another name.`;
  }
  return e.message;
}

/**
 * The company's role matrix (spec F §7.2): the three editable built-in roles,
 * then the company's CUSTOM roles (2026-09-15), × thirteen grantable keys.
 * Owner is not a row — it is everything. A tick saves at once (one PUT per
 * change, the whole row) — the raw picked set is sent as-is; crm-api applies
 * the SAME closure rule on receipt and the refreshed matrix (after
 * invalidation) reflects whatever it re-derives, so a permission an implier
 * still forces on reappears checked rather than silently vanishing
 * client-side.
 *
 * Built-ins: "Reset to default" deletes the company's row so the shipped
 * default applies again — including future changes to that default. Custom
 * roles have no default: they start empty, can be RENAMED (the key crm-api
 * minted stays, so no membership is rewritten) and DELETED — behind a
 * `<ConfirmDialog>`, and refused by crm-api while anyone still holds the role,
 * which this panel explains inline rather than as a bare 409.
 *
 * Rows come from `matrixRows`, which also covers a crm-api that predates
 * custom roles (no `custom` / `label` / `order`): built-ins only, labels from
 * the shared constant.
 *
 * Collapsed by default and wrapped in an `Accordion` (unlike the portal's
 * always-open `RoleMatrixCard`, which it otherwise mirrors) because this page
 * shows it above a roster that is the operator's primary task here.
 */
export function RoleMatrixPanel({
  companyName,
  matrix,
  saving,
  readOnly,
  onChange,
  onReset,
  onCreate,
  onRename,
  onDelete,
}: {
  companyName: string;
  matrix: ResellerRoleMatrix;
  saving: boolean;
  /** True for staff with `crm:resellers` but not `crm:edit` — spec §8: they
   * may see the matrix but not change it. Every checkbox is disabled, and
   * "Reset to default", Add role, Rename and Delete are hidden rather than
   * merely disabled, since there is nothing for them to do. */
  readOnly?: boolean;
  onChange: (role: MatrixRoleKey, permissions: string[]) => void;
  onReset: (role: EditableResellerRole) => void;
  /** Create a custom role. Reject to keep the typed name and show why. */
  onCreate: (name: string) => Promise<unknown>;
  onRename: (role: CustomResellerRoleKey, name: string, permissions: string[]) => Promise<unknown>;
  /** Delete a custom role. A rejection (e.g. 409 ROLE_IN_USE) is shown inline. */
  onDelete: (role: CustomResellerRoleKey) => Promise<unknown>;
}): JSX.Element {
  const rows = matrixRows(matrix);
  const [newName, setNewName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ key: CustomResellerRoleKey; draft: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ResellerRoleRow | null>(null);

  const submitCreate = async (e: FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreateError(null);
    try {
      await onCreate(name);
      setNewName("");
    } catch (err) {
      setCreateError(roleWriteError(err, companyName, name));
    }
  };

  const submitRename = async (row: ResellerRoleRow) => {
    if (!renaming || row.key !== renaming.key) return;
    const name = renaming.draft.trim();
    if (!name || name === row.label) {
      setRenaming(null);
      return;
    }
    setRowError(null);
    try {
      await onRename(renaming.key, name, row.permissions);
      setRenaming(null);
    } catch (err) {
      setRowError(roleWriteError(err, companyName, name));
    }
  };

  return (
    <Accordion disableGutters>
      <AccordionSummary
        expandIcon={<ChevronDown size={16} />}
        data-cy="reseller-role-matrix"
        sx={{ "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Roles at {companyName}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Box sx={{ px: 2.5, pt: 1, pb: 1.5, display: "grid", gap: 1.5 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Owners can do everything and cannot be narrowed. Ticking <code>edit</code> also grants{" "}
            <code>view</code>; Licences and invoice editing also grant Customers view.
          </Typography>

          {!readOnly && (
            <Box
              component="form"
              onSubmit={submitCreate}
              sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 1 }}
            >
              <TextField
                size="small"
                label="New role"
                placeholder="e.g. Field engineer"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                inputProps={{ maxLength: CUSTOM_ROLE_NAME_MAX, "data-cy": "reseller-role-name" }}
                helperText={`A new role starts with no permissions — tick them below, then assign it to people at ${companyName}.`}
                sx={{ minWidth: 240, flex: "1 1 320px" }}
              />
              <Button
                type="submit"
                variant="outlined"
                startIcon={<Plus size={14} />}
                disabled={saving || newName.trim().length === 0}
                data-cy="reseller-role-create"
                sx={{ mt: 0.25 }}
              >
                Add role
              </Button>
            </Box>
          )}

          {createError && (
            <Alert severity="error" data-testid="role-create-error">
              {createError}
            </Alert>
          )}
          {rowError && (
            <Alert severity="warning" data-testid="role-matrix-error" onClose={() => setRowError(null)}>
              {rowError}
            </Alert>
          )}
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                {GRANTABLE_PERMISSIONS.map((k) => (
                  <TableCell key={k} align="center" sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
                    {k}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const held = new Set(row.permissions);
                const isRenaming = renaming !== null && renaming.key === row.key;
                return (
                  <TableRow key={row.key} data-testid={`matrix-row-${row.key}`} data-custom={row.custom}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {isRenaming ? (
                        <Box
                          component="form"
                          onSubmit={(e: FormEvent) => {
                            e.preventDefault();
                            void submitRename(row);
                          }}
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          <TextField
                            size="small"
                            autoFocus
                            value={renaming.draft}
                            onChange={(e) => setRenaming({ key: renaming.key, draft: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Escape") setRenaming(null);
                            }}
                            inputProps={{
                              maxLength: CUSTOM_ROLE_NAME_MAX,
                              "aria-label": `New name for ${row.label}`,
                              "data-cy": "reseller-role-rename-input",
                            }}
                            sx={{ width: 180 }}
                          />
                          <Button size="small" type="submit" disabled={saving || renaming.draft.trim() === ""}>
                            Save
                          </Button>
                          <Button size="small" color="inherit" onClick={() => setRenaming(null)}>
                            Cancel
                          </Button>
                        </Box>
                      ) : (
                        <>
                          {row.label}
                          {(row.custom || row.isDefault) && (
                            <Box component="span" sx={{ ml: 1, fontSize: 11, color: "text.disabled" }}>
                              {row.custom ? "custom" : "default"}
                            </Box>
                          )}
                        </>
                      )}
                    </TableCell>
                    {GRANTABLE_PERMISSIONS.map((k) => (
                      <TableCell key={k} align="center" padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={held.has(k)}
                          disabled={saving || Boolean(readOnly)}
                          inputProps={{ "aria-label": `${k} for ${row.label}` }}
                          onChange={(e) => {
                            const next = new Set(held);
                            if (e.target.checked) next.add(k);
                            else next.delete(k);
                            // Sent RAW — crm-api is the one that closes it
                            // (spec F §7.2); closing here first would let a
                            // deselected implied key silently reappear
                            // without the operator seeing why.
                            onChange(
                              row.key,
                              GRANTABLE_PERMISSIONS.filter((p) => next.has(p)),
                            );
                          }}
                        />
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {!readOnly && !row.custom && !row.isDefault && (
                        <Button size="small" disabled={saving} onClick={() => onReset(row.key as EditableResellerRole)}>
                          Reset to default
                        </Button>
                      )}
                      {!readOnly && row.custom && !isRenaming && (
                        <>
                          <Button
                            size="small"
                            disabled={saving}
                            aria-label={`Rename ${row.label}`}
                            data-cy="reseller-role-rename"
                            onClick={() => {
                              setRowError(null);
                              setRenaming({ key: row.key as CustomResellerRoleKey, draft: row.label });
                            }}
                          >
                            Rename
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={saving}
                            aria-label={`Delete ${row.label}`}
                            data-cy="reseller-role-delete"
                            onClick={() => {
                              setRowError(null);
                              setPendingDelete(row);
                            }}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </AccordionDetails>

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        variant="destructive"
        title={pendingDelete ? `Delete the “${pendingDelete.label}” role?` : "Delete role?"}
        description={
          pendingDelete
            ? `Removes ${pendingDelete.label} from ${companyName}. A role can only be deleted once nobody holds it — move its people to another role first.`
            : ""
        }
        confirmLabel="Delete role"
        onConfirm={async () => {
          if (!pendingDelete) return;
          const target = pendingDelete;
          try {
            await onDelete(target.key as CustomResellerRoleKey);
          } catch (err) {
            // Closed, not held open: the fix (re-assigning people) happens on
            // the roster below, so the reason stays on the panel instead.
            setRowError(roleWriteError(err, companyName));
          }
        }}
      />
    </Accordion>
  );
}
